<template>
<Modal v-on:action="handleModalAction" />
<Toast />
<NavigationBar
    :class="{ scrolled: isScrolled }"
    :sale-button-variant="saleButtonVariant"
/>
<ClientOnly>
    <ShoppingCartTray />
</ClientOnly>
<slot />
<Promotions v-if="promoBannerLocaleAvailable && !maalnVisible" />
<MAALNBanner :key="route.path" v-on:visible="onMaalnVisible" />
<Footer />
<!-- We are now using CookieYes instead of our custom Cookie Consent banner -->
<!--<CookieConsent />-->
<!-- Zendesk "Gene Bot" has been removed due to their introduction of AI-generated answers that are both
 expensive and unhelpful given our current config.-->
<!-- <Zendesk /> -->
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router'
import { Uuid } from '@jackboxgames/uuid'
import { useRequestURL } from 'nuxt/app'
import { ShoppingCart } from '$services/shopify'
import { useCurrentUser } from '$services/user'
import Promotions from '$components/shopping/promotions/Promotions.vue'
import MAALNBanner from '~/components/homepage/MAALNBanner.vue'
import { CookieYes } from '$services/cookieyes'
import Category = CookieYes.Category

const currentUser = useCurrentUser()
const isScrolled = ref(false)
const scrollbarWidth = ref('unset')
const maalnVisible = ref(false)
const saleButtonVariant = ref(false)
const app = useNuxtApp()
const route = useRoute()

if (import.meta.client) {
    window.addEventListener('resize', updateScrollbarWidth)
}

const cartOpen = computed(() => ShoppingCart.isOpen())

const promoBannerLocaleAvailable: ComputedRef<boolean> = computed(() => {
    const { $i18n } = useNuxtApp()
    const locale = $i18n.locale.value
    switch (locale) {
    case 'en':
    case 'de':
    case 'es':
    case 'es-XL':
    case 'es-419':
    case 'fr':
    case 'it':
    case 'pt-BR':
        return true
    default:
        return false
    }
})

function onMaalnVisible(val: boolean) {
    maalnVisible.value = val
}

useHead({
    meta: [
        {
            property: 'og:locale',
            content: 'en_US'
        },
        {
            property: 'og:type',
            content: 'website'
        },
        {
            property: 'og:site_name',
            content: 'Jackbox Games'
        },
        {
            name: 'twitter:card',
            content: 'summary_large_image'
        },
        {
            name: 'twitter:creator',
            content: '@jackboxgames'
        },
        {
            name: 'twitter:site',
            content: '@jackboxgames'
        },
        {
            name: 'facebook-domain-verification',
            content: 'nuuaxgu655yk14irruvpal0s6f6opm'
        },
        {
            name: 'robots',
            content: 'max-image-preview:large'
        },
        {
            name: 'robots',
            content: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'
        }
    ]
})

function onCookieConsentUpdate(event: CookieYes.ConsentUpdate) {
    const data = event.detail
    let acceptedAnalytics = false
    let acceptedAdvertisement = false

    if (data.accepted.includes(Category.Analytics)) {
        void app.$launchDarkly.setup(route)
        acceptedAnalytics = true
    }
    if (data.accepted.includes(Category.Advertisement)) {
        app.$listrak.setup()
        acceptedAdvertisement = true
    }

    if (acceptedAnalytics && acceptedAdvertisement) {
        window.location.reload()
    }
}

onMounted(() => {
    document.addEventListener('scroll', onScroll)

    // Set up the Font Awesome icons
    const fontAwesomeScript = document.createElement('script')
    fontAwesomeScript.setAttribute('src', 'https://kit.fontawesome.com/72acec670f.js')
    fontAwesomeScript.setAttribute('crossorigin', 'anonymous')
    document.head.appendChild(fontAwesomeScript)

    // Set up JBG uuid identification
    void syncJbgId()

    // Set up Listrak
    app.$listrak.setup()

    // Set up LaunchDarkly
    void app.$launchDarkly.setup(route)

    const flag = app.$launchDarkly.useFlag('sale-button-spotlight-in-navigation-menu', false)
    watch(flag, (value) => {
        saleButtonVariant.value = value
    }, { immediate: true })

    document.addEventListener(CookieYes.ConsentUpdateEvent, ((event) => onCookieConsentUpdate(event as CookieYes.ConsentUpdate)))

    // user authentication
    void refreshUser()
    app.$user.checkLastAuthenticated(currentUser.isLoggedIn())
    // Update scrollbar width for shopping cart interactions
    void updateScrollbarWidth()
})

onUpdated(() => {
    void updateScrollbarWidth()
    app.$launchDarkly.updateContext(route)
})

onUnmounted(() => {
    if (import.meta.client) {
        window.removeEventListener('resize', updateScrollbarWidth)
    }
    document.removeEventListener(CookieYes.ConsentUpdateEvent, ((event) => onCookieConsentUpdate(event as CookieYes.ConsentUpdate)))
})

watch(cartOpen, () => {
    if (cartOpen.value) {
        document.body.classList.add('cart-open')
        document.body.style.paddingRight = scrollbarWidth.value
    } else {
        document.body.classList.remove('cart-open')
        document.body.style.paddingRight = 'unset'
    }
})

async function refreshUser() {
    await app.$user.loadUserData()
}

function onScroll() {
    const scroll = window.scrollY

    if (isScrolled.value && scroll < 30) {
        isScrolled.value = false
    } else if (!isScrolled.value && scroll > 80) {
        isScrolled.value = true
    }
}

function handleModalAction(value: string) {
    if (value === 'logout') {
        void app.$user.logout()
        const { $modal } = useNuxtApp()
        $modal.hideModal()
    }

    if (value === 'updateUserData') {
        void app.$user.loadUserData()
    }
}

function updateScrollbarWidth() {
    // A padding-right value equal to the width of the browser's scrollbar is used
    // when the shopping cart is open to prevent the page from "jumping"
    //
    // We await a small timeout to make sure the page is really loaded all the way
    // before computing the width
    setTimeout(() => {
        scrollbarWidth.value = `${window.innerWidth - document.body.clientWidth}px`
    }, 100)
}

// jbg uuid service (sets a cookie for syncing devices across domains)
async function syncJbgId() {
    try {
        const origin = useRequestURL().origin
        await Uuid.syncId(origin)
    } catch (err) {
        console.warn('Failed to sync jbg_id', err)
    }
}

</script>

<style lang="scss">
*, *::before, *::after {
    box-sizing: border-box;
}

html {
    background: var(--shade-100);
}

#app {
    display: block;
    color: var(--neutral-50);
    font-family: Inter;
    -moz-osx-font-smoothing: grayscale;
    -webkit-font-smoothing: antialiased;
}

body {
    margin: 0px;
    width: 100%;
    background: #181c23;
    background: linear-gradient(180deg, var(--surface-800) 7%,#181c23 36%, var(--surface-900) 71%, var(--shade-100) 100%);

    &.no-scroll {
        overflow: hidden;
        height: 100%;
    }

    &.cart-open {
        overflow-y: hidden;
    }
}

.flex-column {
    display: flex;
    flex-direction: column;
}

.flex-row {
    display: flex;
    flex-direction: row;
}

// grid templates
.grid {
    display: grid;

    &.vertical-center{
        align-items: center;
    }

    &.halves {
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        grid-gap: 20px;
    }
}

// Flicker animation used in jnp screens
@keyframes flicker {
    0% { opacity: 1; }

    10% { opacity: 1; }
    12% { opacity: 0.8; }
    14% { opacity: 1; }

    30% { opacity: 1; }
    32% { opacity: 0.8; }
    34% { opacity: 1; }
    36% { opacity: 0.8; }
    38% { opacity: 1; }

    50% { opacity: 1; }
    52% { opacity: 0.8; }
    54% { opacity: 1; }

    60% { opacity: 1; }
    62% { opacity: 0.8; }
    64% { opacity: 1; }

    72% { opacity: 1; }
    74% { opacity: 0.8; }
    76% { opacity: 1; }

    92% { opacity: 1; }
    94% { opacity: 0.8; }
    96% { opacity: 1; }
    98% { opacity: 0.8; }
    100% { opacity: 1; }
}
</style>
